import React from 'react';
import { Globe, BookOpen, ChevronDown } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';
import { INCOTERMS, VAT_REGIMES, COUNTRY_LABELS } from '../../data/internationalTrade';

/**
 * InternationalTradePanel — pedagogical Incoterm + VAT panel for intra/extra-EU
 * contracts. Extracted from `ContractAssignModal.jsx` (04/06/2026 refactor).
 *
 * 10/07/2026 — retour user prod : le panneau prenait trop de hauteur dans la
 * modale d'affectation (plus de place pour choisir chauffeur/camion). Il est
 * désormais REPLIABLE : une ligne compacte (pays · Incoterm · TVA · douane)
 * toujours visible, détails Incoterm/TVA/pédagogie au clic.
 */
export default function InternationalTradePanel({ contract }) {
  const { t } = useTranslation();
  if (!contract?.international) return null;

  const inc = INCOTERMS[contract.incoterm] || INCOTERMS.CPT;
  const vat = VAT_REGIMES[contract.international] || VAT_REGIMES.intra;
  const country = COUNTRY_LABELS[contract.country] || { name: contract.country || '?', flag: '🌍' };
  const customs = contract.customsFees || 0;

  return (
    <details className="border-b border-ink-600 bg-gradient-to-r from-purple-900/15 to-amber-900/15 group" data-testid="international-panel">
      <summary
        className="px-5 py-2 flex items-center gap-2 cursor-pointer list-none select-none flex-wrap"
        data-testid="international-panel-toggle"
      >
        <Globe className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-wider font-mono text-amber-300 font-bold">
          {t('contract_international_label')} · {country.flag} {country.name}
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold"
          style={{ backgroundColor: inc.color + '20', color: inc.color }}
          data-testid={`incoterm-${inc.code}`}
        >
          {inc.code}
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold"
          style={{ backgroundColor: vat.color + '20', color: vat.color }}
        >
          TVA {Math.round(vat.rate * 100)}%
        </span>
        {customs > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
            {t('contract_customs_fees')} {formatMoney(customs)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-500">
          <span className="group-open:hidden">{t('contract_intl_details_show')}</span>
          <span className="hidden group-open:inline">{t('contract_intl_details_hide')}</span>
          <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" />
        </span>
      </summary>

      <div className="px-5 pb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {/* Incoterm tile */}
          <div className="bg-ink-800/60 border rounded p-2.5" style={{ borderColor: inc.color + '60' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t('contract_incoterm_label')}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold" style={{ backgroundColor: inc.color + '20', color: inc.color }}>
                {inc.code}
              </span>
            </div>
            <p className="text-slate-200 font-semibold leading-tight">{inc.short}</p>
            <p className="text-[10px] text-slate-400 mt-1 leading-snug">
              <strong className="text-slate-300">{t('contract_risk_transfer')}</strong> {inc.transferPoint}<br />
              <strong className="text-slate-300">{t('contract_your_charge')}</strong> {inc.transporterCost}
            </p>
          </div>
          {/* VAT tile */}
          <div className="bg-ink-800/60 border rounded p-2.5" style={{ borderColor: vat.color + '60' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t('contract_vat_applicable')}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold" style={{ backgroundColor: vat.color + '20', color: vat.color }}>
                {Math.round(vat.rate * 100)}%
              </span>
            </div>
            <p className="text-slate-200 font-semibold leading-tight">{vat.short}</p>
            <p className="text-[10px] text-slate-400 mt-1 leading-snug italic">
              {t('contract_invoice_mention')} « {vat.mention} »
            </p>
          </div>
        </div>
        {/* Customs fees if extra-UE */}
        {customs > 0 && (
          <div className="mt-2 px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded text-[10px] text-amber-300 flex items-center justify-between" data-testid="customs-cost-banner">
            <span><strong>{t('contract_customs_fees')}</strong> {t('contract_customs_fees_detail')}</span>
            <span className="font-mono font-bold">{formatMoney(customs)} {t('contract_ht_deducted')}</span>
          </div>
        )}
        {/* Pedagogical takeaway */}
        <div className="text-[10px] text-slate-400 mt-2 italic leading-relaxed space-y-1" data-testid="international-pedagogy">
          <p className="flex items-center gap-1 not-italic text-amber-300 font-medium"><BookOpen className="w-3 h-3" /> {t('contract_why_this_choice')}</p>
          <p><strong className="text-purple-300">Incoterm {inc.code}</strong> — {inc.teachingNote}</p>
          <p><strong className="text-amber-300">TVA</strong> — {vat.teachingNote}</p>
        </div>
      </div>
    </details>
  );
}
